import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { MessageSquare, X, Hash, ArrowDown, Loader2, AtSign } from 'lucide-react';
import { Badge } from '../ui/badge';
import { toast } from '../../hooks/use-toast';
import {
  getChatRooms, getRoomMessages, sendRoomMessage, editRoomMessage, deleteChatMessage,
  markRoomRead, getRoomMembers, toggleReaction, getRoomMentions,
  sendTyping, getTyping, togglePin, getRoomPins, uploadChatImage
} from '../../services/api';
import { RoomList } from './chat/RoomList';
import { MessageRow } from './chat/MessageRow';
import { Composer } from './chat/Composer';
import { MentionInbox } from './chat/MentionInbox';
import { PinnedBar } from './chat/PinnedBar';
import { ImageLightbox } from './chat/ImageLightbox';
import { PushNudge } from './chat/PushNudge';
import { buildTimeline, mergeMessages, latestStamp, toDate } from './chat/chatUtils';

const TIER_COLORS = { free: 'bg-zinc-600', bronze: 'bg-amber-700', silver: 'bg-zinc-400', gold: 'bg-yellow-500' };
const TIER_LABELS = { free: 'Free', bronze: 'Bronze', silver: 'Silver', gold: 'Gold' };
const LAST_ROOM_KEY = 'chat:lastRoom';

const ChatPanel = ({ isOpen, onClose, isMobileOverlay = false, onUnreadChange }) => {
  const { currentUser } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [replyToId, setReplyToId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [firstUnreadId, setFirstUnreadId] = useState(null);
  const [atBottom, setAtBottom] = useState(true);
  const [newBelow, setNewBelow] = useState(0);
  const [mentionsOpen, setMentionsOpen] = useState(false);
  const [mentions, setMentions] = useState({ list: [], unread: 0, loading: false });
  const [flashId, setFlashId] = useState(null);
  const [historyMode, setHistoryMode] = useState(false);
  const [pins, setPins] = useState([]);
  const [typing, setTyping] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const containerRef = useRef(null);
  const panelRef = useRef(null);
  const lastSyncRef = useRef(null);
  const lastMarkedRef = useRef(null);
  const pendingScrollRef = useRef(null);
  const canModerate = ['owner', 'moderator'].includes(currentUser?.role);

  const scrollToBottom = useCallback((smooth = false) => {
    const c = containerRef.current;
    if (c) c.scrollTo({ top: c.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  const bumpSync = (msgs) => {
    const s = latestStamp(msgs);
    if (s && (!lastSyncRef.current || s > lastSyncRef.current)) lastSyncRef.current = s;
  };

  const loadRooms = useCallback(async () => {
    try {
      const data = await getChatRooms();
      setRooms(data);
      setRoom((cur) => {
        if (cur) return data.find((r) => r.id === cur.id) || cur;
        const stored = localStorage.getItem(LAST_ROOM_KEY);
        return data.find((r) => r.id === stored) || data[0] || null;
      });
    } catch { /* silent */ }
  }, []);

  const openRoom = useCallback(async (r) => {
    if (!r) return;
    setLoading(true); setReplyToId(null); setEditingId(null); setNewBelow(0);
    setMentionsOpen(false); setHistoryMode(false); setFlashId(null);
    lastSyncRef.current = null; lastMarkedRef.current = null;
    try {
      let data = await getRoomMessages(r.id, { limit: 60 });
      if (r.lastReadAt && r.unreadCount > 55) {
        const rest = await getRoomMessages(r.id, { since: r.lastReadAt, limit: 500 });
        data = mergeMessages(data, rest);
      }
      const lastRead = toDate(r.lastReadAt);
      const firstUnread = lastRead ? data.find((m) => m.senderId !== currentUser.id && toDate(m.createdAt) > lastRead) : null;
      setFirstUnreadId(firstUnread?.id || null);
      pendingScrollRef.current = firstUnread ? firstUnread.id : 'bottom';
      setMessages(data); bumpSync(data);
    } catch { setMessages([]); }
    finally { setLoading(false); }
    getRoomMembers(r.id).then(setMembers).catch(() => setMembers([]));
    getRoomMentions(r.id).then((d) => setMentions({ list: d.mentions || [], unread: d.unread || 0, loading: false })).catch(() => {});
    getRoomPins(r.id).then(setPins).catch(() => setPins([]));
    setTyping([]);
  }, [currentUser?.id]);

  useEffect(() => { if (currentUser && isOpen) loadRooms(); }, [currentUser, isOpen, loadRooms]);

  useEffect(() => {
    if (!room || !isOpen) return;
    localStorage.setItem(LAST_ROOM_KEY, room.id);
    openRoom(room);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room?.id, isOpen]);

  // Scroll to where the reader left off (or bottom) once the room renders
  useEffect(() => {
    if (loading || !pendingScrollRef.current) return;
    const target = pendingScrollRef.current; pendingScrollRef.current = null;
    requestAnimationFrame(() => {
      const el = target !== 'bottom' && containerRef.current?.querySelector(target.startsWith('msg:') ? `[data-testid="chat-message-${target.slice(4)}"]` : '[data-testid="new-messages-divider"]');
      if (el) el.scrollIntoView({ block: target.startsWith('msg:') ? 'center' : 'start' }); else scrollToBottom();
    });
  }, [loading, messages, scrollToBottom]);

  // Incremental sync: only what changed since last poll (new / edited / deleted)
  useEffect(() => {
    if (!room || !isOpen) return undefined;
    const tick = async () => {
      if (!lastSyncRef.current || document.visibilityState !== 'visible') return;
      getTyping(room.id).then((d) => setTyping(d.typing || [])).catch(() => {});
      try {
        const delta = await getRoomMessages(room.id, { since: lastSyncRef.current, limit: 200 });
        if (!delta.length) return;
        if (delta.some((m) => m.pinned !== undefined || m.deleted)) getRoomPins(room.id).then(setPins).catch(() => {});
        setMessages((prev) => {
          const known = new Set(prev.map((m) => m.id));
          const fresh = delta.filter((m) => !m.deleted && !known.has(m.id) && m.senderId !== currentUser.id).length;
          if (fresh && !atBottom) setNewBelow((n) => n + fresh);
          return mergeMessages(prev, delta);
        });
        bumpSync(delta);
        if (atBottom) requestAnimationFrame(() => scrollToBottom(true));
      } catch { /* silent */ }
    };
    const id = setInterval(tick, 3000);
    return () => clearInterval(id);
  }, [room, isOpen, atBottom, currentUser?.id, scrollToBottom]);

  // Mark read when the reader has actually reached the latest message (not while browsing history)
  useEffect(() => {
    if (!room || !isOpen || !atBottom || loading || historyMode || !messages.length) return;
    const latest = messages[messages.length - 1];
    if (lastMarkedRef.current === latest.id) return;
    lastMarkedRef.current = latest.id;
    markRoomRead(room.id, latest.id).then(() => {
      setRooms((rs) => rs.map((r) => (r.id === room.id ? { ...r, unreadCount: 0 } : r)));
      setMentions((m) => ({ ...m, unread: 0 }));
      onUnreadChange?.();
    }).catch(() => {});
  }, [messages, atBottom, room, isOpen, loading, historyMode, onUnreadChange]);

  // Esc closes (backdrop click handled on the overlay itself)
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e) => { if (e.key === 'Escape' && !replyToId && !editingId && !lightbox) onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose, replyToId, editingId, lightbox]);

  const handleScroll = () => {
    const c = containerRef.current; if (!c) return;
    const near = c.scrollHeight - c.scrollTop - c.clientHeight < 80;
    setAtBottom(near);
    if (near) setNewBelow(0);
  };

  const fail = (e, fallback) => toast({ title: 'Chat', description: e?.response?.data?.detail || fallback, variant: 'destructive' });

  const send = async (text, mentionIds, replyTo = null, image = null) => {
    try {
      const sent = await sendRoomMessage(room.id, text, replyTo?.id || null, mentionIds, image);
      setMessages((prev) => mergeMessages(prev, [sent])); bumpSync([sent]);
      setReplyToId(null); setAtBottom(true);
      requestAnimationFrame(() => scrollToBottom(true));
    } catch (e) { fail(e, 'Failed to send message'); throw e; }
  };

  const saveEdit = async (id, text, mentionIds) => {
    try {
      const res = await editRoomMessage(room.id, id, text, mentionIds);
      setMessages((prev) => mergeMessages(prev, [res])); bumpSync([res]);
      setEditingId(null);
    } catch (e) { fail(e, 'Failed to edit message'); throw e; }
  };

  const remove = async (id) => {
    try { await deleteChatMessage(id); setMessages((prev) => prev.filter((m) => m.id !== id)); }
    catch (e) { fail(e, 'Failed to delete message'); }
  };

  const react = async (id, emoji) => {
    try {
      const res = await toggleReaction(room.id, id, emoji);
      setMessages((prev) => mergeMessages(prev, [res])); bumpSync([res]);
    } catch (e) { fail(e, 'Failed to react'); }
  };

  const openMentions = async () => {
    setMentionsOpen(true);
    setMentions((m) => ({ ...m, loading: true }));
    try { const d = await getRoomMentions(room.id); setMentions({ list: d.mentions || [], unread: d.unread || 0, loading: false }); }
    catch { setMentions((m) => ({ ...m, loading: false })); }
  };

  const jumpTo = async (target) => {
    setMentionsOpen(false); setReplyToId(null); setEditingId(null);
    if (!messages.some((m) => m.id === target.id)) {
      try {
        const win = await getRoomMessages(room.id, { around: target.id, limit: 60 });
        setMessages(win); lastSyncRef.current = null; bumpSync(win); setHistoryMode(true); setAtBottom(false);
        pendingScrollRef.current = `msg:${target.id}`;
      } catch (e) { fail(e, 'Could not load that message'); return; }
    } else {
      requestAnimationFrame(() => containerRef.current?.querySelector(`[data-testid="chat-message-${target.id}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
    }
    setFlashId(target.id);
    setTimeout(() => setFlashId(null), 1800);
  };

  const backToLatest = () => { setAtBottom(true); setNewBelow(0); if (historyMode) openRoom(room); else scrollToBottom(true); };

  const pin = async (id) => {
    try {
      const res = await togglePin(room.id, id);
      setMessages((prev) => mergeMessages(prev, [res])); bumpSync([res]);
      setPins(await getRoomPins(room.id));
    } catch (e) { fail(e, 'Failed to pin message'); }
  };

  const heartbeat = () => { if (room) sendTyping(room.id).catch(() => {}); };
  const everyoneLabel = canModerate && room
    ? (room.minTier === 'free' ? 'every member, free and paid' : `every ${TIER_LABELS[room.minTier]}${room.minTier === 'gold' ? '' : '+'} member`)
    : null;
  const uploadImage = room?.allowImages ? async (file) => {
    try { return await uploadChatImage(room.id, file); }
    catch (e) { fail(e, 'Image upload failed'); throw e; }
  } : null;
  const typingLabel = typing.length === 0 ? '' : typing.length === 1 ? `${typing[0].displayName} is typing…`
    : typing.length === 2 ? `${typing[0].displayName} and ${typing[1].displayName} are typing…` : 'Several people are typing…';

  if (!currentUser) return null;

  const timeline = buildTimeline(messages, room?.lastReadAt, currentUser.id, firstUnreadId);
  const inlineActive = replyToId || editingId;

  const body = (
    <div className={`flex flex-1 min-h-0 overflow-hidden ${isMobileOverlay ? 'flex-col' : ''}`}>
      <RoomList rooms={rooms} selectedId={room?.id} onSelect={setRoom} horizontal={isMobileOverlay} />
      <div className="flex-1 flex flex-col min-w-0 min-h-0 bg-[#121215]">
        {room ? (
          <>
            <div className="h-10 px-4 border-b border-zinc-800/80 flex items-center gap-2 shrink-0">
              <Hash className="w-4 h-4 text-amber-500" />
              <span className="font-headline text-zinc-100 text-sm tracking-wide">{room.name}</span>
              {room.minTier !== 'free' && <Badge className={`${TIER_COLORS[room.minTier]} text-black text-[10px] py-0`}>{TIER_LABELS[room.minTier]}+</Badge>}
              <button onClick={mentionsOpen ? () => setMentionsOpen(false) : openMentions} data-testid="mentions-toggle"
                className={`ml-auto flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-headline tracking-wider transition-colors ${mentionsOpen ? 'bg-amber-600/20 text-amber-400' : 'text-zinc-400 hover:text-amber-400 hover:bg-zinc-800/60'}`} title="Messages that mention you">
                <AtSign className="w-3.5 h-3.5" />MENTIONS
                {mentions.unread > 0 && <span className="bg-amber-600 text-black font-bold text-[10px] px-1.5 rounded-full leading-4" data-testid="mentions-unread-badge">{mentions.unread}</span>}
              </button>
            </div>

            <div className="flex-1 relative min-h-0 flex flex-col">
            {mentionsOpen && <MentionInbox mentions={mentions.list} loading={mentions.loading} onJump={jumpTo} onClose={() => setMentionsOpen(false)} />}
            <PinnedBar pins={pins} onJump={jumpTo} canModerate={canModerate} onUnpin={pin} />
            <div ref={containerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto py-2 relative" data-testid="chat-messages-container">
              {loading ? (
                <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-amber-500 animate-spin" /></div>
              ) : timeline.length === 0 ? (
                <div className="text-center py-12 text-zinc-500 font-typewriter">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No evidence logged yet.</p>
                  <p className="text-xs text-zinc-600 mt-1">Be the first to speak.</p>
                </div>
              ) : timeline.map((item) => {
                if (item.kind === 'day') return (
                  <div key={item.id} className="flex items-center my-3 px-4 font-typewriter text-[11px] tracking-widest text-amber-500/80 uppercase before:flex-1 before:border-t before:border-zinc-800 before:mr-3 after:flex-1 after:border-t after:border-zinc-800 after:ml-3" data-testid="day-divider">{item.label}</div>
                );
                if (item.kind === 'new') return (
                  <div key={item.id} className="flex items-center my-2 px-4 text-red-500 font-mono text-[11px] font-bold tracking-wider uppercase before:flex-1 before:border-t-2 before:border-red-600/60 before:mr-3 after:flex-1 after:border-t-2 after:border-red-600/60 after:ml-3" data-testid="new-messages-divider">New messages</div>
                );
                const m = item.msg;
                return (
                  <MessageRow key={m.id} msg={m} grouped={item.grouped} me={currentUser} members={members} flash={flashId === m.id}
                    canDelete={canModerate || m.senderId === currentUser.id} canPin={canModerate} onPin={pin} onTyping={heartbeat}
                    onOpenImage={setLightbox} onUploadImage={uploadImage} everyoneLabel={everyoneLabel}
                    isEditing={editingId === m.id} isReplying={replyToId === m.id}
                    onReply={(msg) => { setEditingId(null); setReplyToId(msg.id); }}
                    onEdit={(id) => { setReplyToId(null); setEditingId(id); }}
                    onDelete={remove} onSaveEdit={saveEdit} onCancelEdit={() => setEditingId(null)} onReact={react}
                    onSendReply={(msg, text, ids, image) => send(text, ids, msg, image)} onCancelReply={() => setReplyToId(null)} />
                );
              })}
            </div>

            {typingLabel && (
              <div className="px-4 py-1 text-[11px] font-typewriter text-amber-500/80 flex items-center gap-1.5 shrink-0" data-testid="typing-indicator">
                <span className="flex gap-0.5"><span className="w-1 h-1 rounded-full bg-amber-500 animate-bounce" /><span className="w-1 h-1 rounded-full bg-amber-500 animate-bounce [animation-delay:150ms]" /><span className="w-1 h-1 rounded-full bg-amber-500 animate-bounce [animation-delay:300ms]" /></span>
                {typingLabel}
              </div>
            )}

            {(newBelow > 0 || !atBottom || historyMode) && !loading && (
              <button onClick={backToLatest} data-testid="jump-to-latest"
                className="absolute bottom-3 left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0 md:right-6 flex items-center gap-1.5 bg-amber-600 text-black text-[11px] font-headline tracking-wider px-3 py-1.5 rounded-full shadow-lg hover:bg-amber-500 transition-colors z-10">
                <ArrowDown className="w-3.5 h-3.5" />{newBelow > 0 ? `${newBelow} new` : historyMode ? 'Back to latest' : 'Latest'}
              </button>
            )}
            </div>

            <PushNudge />
            <div className="p-3 bg-[#121215] border-t border-zinc-800/80 shrink-0">
              {inlineActive ? (
                <div className="flex items-center justify-between text-xs font-typewriter text-zinc-500 px-1 py-2" data-testid="inline-active-hint">
                  <span>{editingId ? 'Editing your message above…' : 'Replying above…'}</span>
                  <button onClick={() => { setReplyToId(null); setEditingId(null); }} className="text-amber-500 hover:text-amber-300 underline" data-testid="inline-cancel-btn">cancel</button>
                </div>
              ) : (
                <Composer members={members} onSubmit={(text, ids, image) => send(text, ids, null, image)} onTyping={heartbeat} onUploadImage={uploadImage} everyoneLabel={everyoneLabel} placeholder={`Message #${room.name}`} />
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-zinc-500 font-typewriter text-sm">Select a channel</div>
        )}
      </div>
    </div>
  );

  const header = (
    <div className="h-12 px-4 border-b border-zinc-800/80 bg-[#121215] flex items-center justify-between shrink-0">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-amber-500" />
        <h2 className="font-headline text-zinc-100 tracking-wider text-sm">EVIDENCE ROOM</h2>
      </div>
      <button onClick={onClose} className="h-7 w-7 flex items-center justify-center rounded text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 transition-colors" data-testid="chat-close-btn"><X className="w-4 h-4" /></button>
    </div>
  );

  if (isMobileOverlay) {
    if (!isOpen) return null;
    return <div ref={panelRef} className="flex flex-col h-full relative" data-testid="chat-dropdown-panel">{header}{body}<ImageLightbox url={lightbox} onClose={() => setLightbox(null)} /></div>;
  }

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div key="chat-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}
          className="hidden md:flex fixed inset-0 z-50 bg-black/70 backdrop-blur-[2px] items-center justify-center p-6"
          onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="chat-backdrop">
          <motion.div ref={panelRef}
            initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.98 }} transition={{ duration: 0.2 }}
            className="flex w-[min(1400px,100%)] h-full max-h-[calc(100vh-3rem)] bg-[#121215] border border-zinc-800 rounded-xl shadow-2xl shadow-black/90 flex-col overflow-hidden"
            data-testid="chat-dropdown-panel">
            {header}{body}
          </motion.div>
          <ImageLightbox url={lightbox} onClose={() => setLightbox(null)} />
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ChatPanel;
